/**
 * StickyNoteNode - Editable sticky note for whiteboard
 *
 * Features:
 * - Double-click to edit text
 * - NodeResizer for resize handles
 * - Handles on all sides for connections
 * - Color customization (via property panel in Plan 05)
 *
 * IMPORTANT: This component must be used with memo() to prevent unnecessary
 * re-renders. React Flow uses reference equality for node type detection.
 */
import { memo, useState, useCallback, type ChangeEvent, type KeyboardEvent } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, type Node } from '@xyflow/react';

// ============================================================================
// NODE DATA TYPES
// ============================================================================

/**
 * StickyNoteData - Data shape stored in Y.Map for sticky notes
 *
 * @property text - The note's text content (supports multi-line)
 * @property color - Background color (defaults to yellow)
 * @property width - Optional width override (default: 200px)
 * @property height - Optional height override (default: 150px)
 * @property aiGenerated - Optional flag indicating this node was created by AI
 * @property generatedAt - Optional ISO timestamp of when AI created this node
 * @property model - Optional AI model name that created this node
 *
 * Note: Index signature required for React Flow Node<T> type compatibility
 */
export interface StickyNoteData {
  text: string;
  color: string;
  width?: number;
  height?: number;
  // AI attribution properties
  aiGenerated?: boolean;
  generatedAt?: string;
  model?: string;
  // Index signature for React Flow compatibility
  [key: string]: unknown;
}

/**
 * StickyNoteNodeType - Full node type for type-safe React Flow usage
 *
 * Use this type when working with sticky notes in React Flow:
 * - In nodeTypes registry
 * - When creating new sticky notes
 * - In onNodesChange handlers
 */
export type StickyNoteNodeType = Node<StickyNoteData, 'stickyNote'>;

// ============================================================================
// STICKY NOTE COLORS
// ============================================================================

/**
 * STICKY_COLORS - Material Design 200-level pastel colors
 *
 * These are lighter shades that work well as backgrounds with dark text.
 * Used for color picker in property panel (Plan 05).
 */
export const STICKY_COLORS = {
  yellow: '#fef08a',   // Yellow 200 - Default sticky note color
  orange: '#fed7aa',   // Orange 200
  green: '#bbf7d0',    // Green 200
  blue: '#bfdbfe',     // Blue 200
  purple: '#ddd6fe',   // Purple 200
  pink: '#fbcfe8',     // Pink 200
} as const;

// ============================================================================
// STICKY NOTE NODE COMPONENT
// ============================================================================

/**
 * StickyNoteNode - The actual sticky note component
 *
 * Rendering behavior:
 * - Default state: Shows text with "Double-click to edit" placeholder
 * - Edit state: Shows textarea for text input
 * - Selected state: Shows resize handles on corners
 *
 * Edit workflow:
 * 1. User double-clicks note
 * 2. Textarea appears with current text
 * 3. User types changes
 * 4. Enter key or blur commits the text
 * 5. Y.Map update happens via onNodesChange in BoardCanvas
 */
export const StickyNoteNode = memo(({ id, data, selected, dragging }: NodeProps<StickyNoteNodeType>) => {
  // ---------------------------------------------------------------------------
  // LOCAL EDITING STATE
  // Note: editText is local until blur/Enter, then synced via React Flow
  // ---------------------------------------------------------------------------
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(data.text);

  // ---------------------------------------------------------------------------
  // DYNAMIC SHADOW based on node state
  // ---------------------------------------------------------------------------
  const getShadowStyle = () => {
    if (dragging) {
      return 'var(--shadow-elevation-high)';
    }
    if (selected) {
      return 'var(--shadow-elevation-medium)';
    }
    return 'var(--shadow-elevation-low)';
  };

  // ---------------------------------------------------------------------------
  // EVENT HANDLERS
  // ---------------------------------------------------------------------------

  /**
   * Handle text change during edit
   * Updates local state only - no sync until commit
   */
  const handleTextChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setEditText(e.target.value);
  }, []);

  /**
   * Commit edit on blur
   * The actual Y.Map update happens via onNodesChange in BoardCanvas
   * which receives the updated data from React Flow
   */
  const handleBlur = useCallback(() => {
    setIsEditing(false);
    // Note: Parent component should handle the data update
    // This is typically done by detecting the text change and
    // updating the Y.Map in BoardCanvas
  }, []);

  /**
   * Handle keyboard events during edit
   * - Enter: Commit edit (blur the textarea)
   * - Shift+Enter: Insert newline (don't commit)
   * - Delete/Backspace: Stop propagation to prevent node deletion
   */
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter without Shift commits the edit
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.blur();  // Triggers handleBlur
    }

    // CRITICAL: Stop Delete/Backspace from bubbling up to React Flow
    // Without this, pressing Backspace while editing would delete the node
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.stopPropagation();
    }
  }, []);

  /**
   * Double-click activates edit mode
   * Resets editText to current data.text to ensure fresh state
   */
  const handleDoubleClick = useCallback(() => {
    setEditText(data.text);
    setIsEditing(true);
  }, [data.text]);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* ===================================================================
          RESIZE HANDLES
          NodeResizer provides drag-to-resize functionality.
          Only visible when the node is selected.
          =================================================================== */}
      <NodeResizer
        minWidth={120}
        minHeight={80}
        isVisible={selected}
        lineClassName="border-blue-500"
        handleClassName="bg-blue-500 border-2 border-white rounded"
      />

      {/* ===================================================================
          CONNECTION HANDLES
          Handles on all 4 sides allow flexible edge connections.
          - Top/Left: target (incoming connections)
          - Bottom/Right: source (outgoing connections)
          =================================================================== */}
      <Handle type="target" position={Position.Top} id="top" className="w-3 h-3" />
      <Handle type="source" position={Position.Bottom} id="bottom" className="w-3 h-3" />
      <Handle type="target" position={Position.Left} id="left" className="w-3 h-3" />
      <Handle type="source" position={Position.Right} id="right" className="w-3 h-3" />

      {/* ===================================================================
          STICKY NOTE CONTENT
          The visual sticky note with background color and text.
          =================================================================== */}
      <div
        className="relative w-full h-full p-3 rounded-lg cursor-pointer"
        style={{
          // Use provided color or default to yellow
          backgroundColor: data.color || STICKY_COLORS.yellow,
          // Respect stored dimensions or use defaults
          minWidth: data.width || 200,
          minHeight: data.height || 150,
          // Dynamic shadow based on drag/select state
          boxShadow: getShadowStyle(),
          // Smooth shadow transition + scale effect
          transition: dragging
            ? 'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'box-shadow 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275), transform 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          // Slight scale up when dragging
          transform: dragging ? 'scale(1.02)' : 'scale(1)',
        }}
        onDoubleClick={handleDoubleClick}
      >
        {/* -------------------------------------------------------------------
            AI GENERATED BADGE
            Shows sparkle icon when node was created by AI.
            Hover shows timestamp if available.
            ------------------------------------------------------------------- */}
        {data.aiGenerated && (
          <div
            className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-violet-500 rounded-full"
            title={`AI generated${data.model ? ` by ${data.model}` : ''}${data.generatedAt ? ` at ${new Date(data.generatedAt).toLocaleString()}` : ''}`}
          >
            {/* Sparkle icon - SVG for crisp rendering */}
            <svg
              className="w-3 h-3 text-white"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
            </svg>
          </div>
        )}
        {/* -------------------------------------------------------------------
            EDIT MODE: Textarea for text input
            ------------------------------------------------------------------- */}
        {isEditing ? (
          <textarea
            className="w-full h-full bg-transparent resize-none outline-none text-sm"
            value={editText}
            onChange={handleTextChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoFocus
            style={{ color: '#000000' }}  // Black text for readability on pastel backgrounds
          />
        ) : (
          /* -----------------------------------------------------------------
             VIEW MODE: Static text display
             ----------------------------------------------------------------- */
          <p
            className="text-sm whitespace-pre-wrap break-words"
            style={{ color: '#000000' }}  // Black text for readability
          >
            {data.text || 'Double-click to edit'}
          </p>
        )}
      </div>
    </>
  );
});

// Display name for React DevTools debugging
StickyNoteNode.displayName = 'StickyNoteNode';
